import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSubmenu = (menuLabel) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuLabel]: !prev[menuLabel],
    }));
  };

  const handleToggleSidebar = () => {
    // On mobile (<768px), toggle sidebarOpen (slide in/out)
    // On desktop (>768px), toggle sidebarCollapsed (70px <-> 250px)
    if (window.innerWidth <= 768) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const isAdmin = user?.is_admin || false;

  const isActive = (path) => location.pathname === path;

  const clientMenuItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'bx-home-circle' },
    { label: 'Portfolio', path: '/portfolio', icon: 'bx-money' },
    { label: 'Bonds', path: '/bonds', icon: 'bx-folder-open' },
    { label: 'Treasury Bonds', path: '/treasury-bonds', icon: 'bx-buildings' },
    { label: 'Equities', path: '/equities', icon: 'bx-dollar-circle' },
    { label: 'Certificate of Deposits', path: '/gics', icon: 'bx-time' },
    { label: 'Request Deposit', path: '/deposits/new', icon: 'bx-transfer' },
    { label: 'Request Withdrawal', path: '/withdrawals/new', icon: 'bx-transfer' },
    { label: 'Profile', path: '/profile', icon: 'bx-user' },
    
  ];

  const adminMenuItems = [
    { label: 'Admin Dashboard', path: '/admin', icon: 'bx-bar-chart' },
    {
      label: 'Clients',
      icon: 'bx-user',
      submenu: [
        { label: 'View Clients', path: '/clients' },
        { label: 'Add Client', path: '/clients/new' },
        { label: 'Payment Details', path: '/bank-details-list' },
      ],
    },
    {
      label: 'Bonds',
      icon: 'bx-archive',
      submenu: [
        { label: 'View Products', path: '/bonds' },
        { label: 'Bond Orders', path: '/order-bonds' },
      ],
    },
    {
      label: 'Treasury Bonds',
      icon: 'bx-buildings',
      submenu: [
        { label: 'View Products', path: '/treasury-bonds' },
        { label: 'TB Orders', path: '/order-treasury-bonds' },
      ],
    },
    {
      label: 'Equities',
      icon: 'bx-dollar-circle',
      submenu: [
        { label: 'View Equities', path: '/equities' },
        { label: 'Equity Orders', path: '/order-equities' },
      ],
    },
    { label: 'Certificate of Deposits', path: '/gics', icon: 'bx-time' },
    {
      label: 'Deposits',
      icon: 'bx-money',
      submenu: [
        { label: 'View Deposits', path: '/deposits' },
        { label: 'Add Deposit', path: '/deposits/new' },
      ],
    },
    {
      label: 'Withdrawals',
      icon: 'bx-money-withdraw',
      submenu: [
        { label: 'View Withdrawals', path: '/withdrawals' },
      ],
    },
    { label: 'Profile', path: '/profile', icon: 'bx-user' },
  ];

  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  return (
    <div className="layout-wrapper">
      {/* Header */}
      <header className="page-topbar">
        <div className="navbar-header">
          <div className="navbar-flex">
            <div className="navbar-brand-box">
              <Link to={isAdmin ? '/admin' : '/dashboard'} className="logo">
                <img src={`${(process.env.REACT_APP_API_URL || 'https://backoffice.bnywm-cp.com/api/v1').replace(/\/api\/v1$/, '')}/images/bny-logo.png`} alt="BNY Logo" className="logo-img" />
              </Link>
            </div>
            <button
              className="sidebar-toggle-btn"
              onClick={handleToggleSidebar}
              aria-label="Toggle sidebar"
              title="Toggle sidebar"
            >
              <i className="bx bx-menu"></i>
            </button>
            <div className="account-name">
              <span>Managed Account</span>
            </div>
          </div>

          <div className="navbar-right">
            <div className="user-dropdown">
              <button
                className="user-dropdown-toggle"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              >
                <span className="user-avatar">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
                <i className="bx bx-chevron-down"></i>
              </button>

              {userDropdownOpen && (
                <div className="dropdown-menu">
                  <Link
                    to={isAdmin ? '/admin' : '/dashboard'}
                    className="dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <i className="bx bx-home-circle"></i>
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <i className="bx bx-user"></i>
                    <span>Profile</span>
                  </Link>
                  <div className="dropdown-divider"></div>
                  <button
                    onClick={handleLogout}
                    className="dropdown-item logout-btn"
                  >
                    <i className="bx bx-log-out"></i>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="layout-container">
        {/* Sidebar */}
        <aside
          className={`vertical-menu ${sidebarOpen ? 'active' : ''} ${
            sidebarCollapsed ? 'collapsed' : ''
          }`}
        >
          <div className="sidebar-content">
            <div className="sidebar-menu">
              <ul className="metismenu">
                <li className="menu-title">
                  {isAdmin ? 'ADMIN MENU' : 'CLIENT MENU'}
                </li>

                {menuItems.map((item, index) => (
                  <li
                    key={index}
                    className={openMenus[item.label] ? 'open' : ''}
                  >
                    {item.submenu ? (
                      <>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            toggleSubmenu(item.label);
                          }}
                          className="has-arrow"
                        >
                          <i className={`bx ${item.icon}`}></i>
                          <span>{item.label}</span>
                        </a>
                        <ul className="sub-menu">
                          {item.submenu.map((subitem, subindex) => (
                            <li key={subindex}>
                              <Link
                                to={subitem.path}
                                className={isActive(subitem.path) ? 'active' : ''}
                                onClick={() => setSidebarOpen(false)}
                              >
                                {subitem.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className={`waves-effect ${
                          isActive(item.path) ? 'active' : ''
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <i className={`bx ${item.icon}`}></i>
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </li>
                ))}

                <li>
                  <button
                    onClick={handleLogout}
                    className="logout-link"
                  >
                    <i className="bx bx-log-out"></i>
                    <span>Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {children}
            </div>
          </div>

          {/* Footer */}
          <footer className="footer">
            <div className="container-fluid">
              <div className="footer-content">
                <div className="footer-left"></div>
                <div className="footer-right">2026 © Client Portal</div>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* Close sidebar overlay on mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Layout;
